/**
 * Session Log Store — mirrors admin/staff login/logout to both localStorage
 * (fast) and `session_logs` table (persistent, DB-backed admin viewer).
 */
import { supabase } from "@/integrations/supabase/client";

export interface SessionLog {
  id: string;
  userId: string;
  email: string;
  username: string;
  role: string;
  loginAt: number;
  logoutAt: number | null;
  duration: number | null;
  logoutReason: "manual" | "page_leave" | "session_expired" | null;
  dbId?: string;
}

const SESSION_LOG_KEY = "khays_session_logs";
const ACTIVE_SESSION_KEY = "khays_active_session";

function loadSessionLogs(): SessionLog[] {
  try { const s = localStorage.getItem(SESSION_LOG_KEY); if (s) return JSON.parse(s); } catch {}
  return [];
}

function saveSessionLogs(logs: SessionLog[]): void {
  localStorage.setItem(SESSION_LOG_KEY, JSON.stringify(logs.slice(-500)));
}

async function insertDbSession(session: SessionLog): Promise<string | undefined> {
  try {
    const { data } = await supabase.from("session_logs").insert({
      user_id: session.userId,
      email: session.email || null,
      username: session.username || null,
      role: session.role || null,
      login_at: new Date(session.loginAt).toISOString(),
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    } as any).select("id").single();
    return (data as any)?.id;
  } catch { return undefined; }
}

async function updateDbSession(dbId: string, logoutAt: number, duration: number, reason: string) {
  try {
    await supabase.from("session_logs").update({
      logout_at: new Date(logoutAt).toISOString(),
      duration_seconds: duration,
      logout_reason: reason,
    } as any).eq("id", dbId);
  } catch {}
}

export function startSession(userId: string, email: string, username: string, role: string): void {
  closeActiveSession("session_expired");
  const session: SessionLog = {
    id: `sess_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    userId, email, username, role,
    loginAt: Date.now(),
    logoutAt: null, duration: null, logoutReason: null,
  };
  localStorage.setItem(ACTIVE_SESSION_KEY, JSON.stringify(session));
  const logs = loadSessionLogs();
  logs.push(session);
  saveSessionLogs(logs);
  // Fire-and-forget DB insert
  insertDbSession(session).then(dbId => {
    if (!dbId) return;
    try {
      const raw = localStorage.getItem(ACTIVE_SESSION_KEY);
      if (!raw) return;
      const cur: SessionLog = JSON.parse(raw);
      cur.dbId = dbId;
      localStorage.setItem(ACTIVE_SESSION_KEY, JSON.stringify(cur));
    } catch {}
  });
}

export function closeActiveSession(reason: "manual" | "page_leave" | "session_expired"): void {
  try {
    const raw = localStorage.getItem(ACTIVE_SESSION_KEY);
    if (!raw) return;
    const active: SessionLog = JSON.parse(raw);
    active.logoutAt = Date.now();
    active.duration = Math.round((active.logoutAt - active.loginAt) / 1000);
    active.logoutReason = reason;
    const logs = loadSessionLogs();
    const idx = logs.findIndex(l => l.id === active.id);
    if (idx >= 0) logs[idx] = active; else logs.push(active);
    saveSessionLogs(logs);
    localStorage.removeItem(ACTIVE_SESSION_KEY);
    if (active.dbId) updateDbSession(active.dbId, active.logoutAt, active.duration, reason);
  } catch {}
}

export function getActiveSession(): SessionLog | null {
  try { const raw = localStorage.getItem(ACTIVE_SESSION_KEY); if (raw) return JSON.parse(raw); } catch {}
  return null;
}

export function getSessionLogs(): SessionLog[] {
  return loadSessionLogs().sort((a, b) => b.loginAt - a.loginAt);
}

export function clearSessionLogs(): void {
  localStorage.removeItem(SESSION_LOG_KEY);
}

export function formatDuration(seconds: number | null): string {
  if (seconds === null) return "Active";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}
